import { Link } from "react-router-dom";
import JsonLd from "@/components/base/JsonLd";
import PageHeader from "@/components/feature/PageHeader";
import useSeo from "@/hooks/useSeo";
import { site } from "@/config/site";
import { testimonials } from "@/mocks/faqs";

const ORIGIN = "https://jyhtdd.cn";

const principles = [
  { icon: "ri-focus-3-line", title: "只做与公众利益相关的事", desc: "私人恩怨、商业诋毁与无关公众利益的信息，不在我们的收稿范围内。" },
  { icon: "ri-scales-3-line", title: "先核实，再开口", desc: "没有两个以上独立来源支撑的指控，不会以「已核实」的状态对外发布。" },
  { icon: "ri-user-shared-line", title: "保护投稿人优先于抢时效", desc: "再热的料，如果需要牺牲投稿人安全才能发，我们宁可等一等。" },
  { icon: "ri-refresh-line", title: "错了就改正，不悄悄删除", desc: "发现事实性错误会在原页面显著位置发布更正说明，保留完整的更正记录。" },
];

export default function AboutPage() {
  useSeo({
    title: "关于我们 - 看料爆料｜匿名线索投稿平台的定位与原则",
    description:
      "了解看料爆料的品牌定位、内容原则与用户口碑。我们是一个接收匿名投稿、快速筛选核实线索的平台，在保证信息可信的同时降低投稿人的曝光风险。",
    keywords: "关于看料爆料,看料爆料平台定位,爆料内容原则,匿名投稿平台,看料爆料团队",
    canonical: `${ORIGIN}/about`,
  });

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      name: "关于看料爆料",
      url: `${ORIGIN}/about`,
      inLanguage: "zh-CN",
    },
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: site.name,
      url: `${ORIGIN}/`,
      email: site.email,
      foundingDate: site.since,
      description: site.intro,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: `${ORIGIN}/` },
        { "@type": "ListItem", position: 2, name: "关于我们", item: `${ORIGIN}/about` },
      ],
    },
  ];

  return (
    <>
      <JsonLd data={jsonLd} />
      <PageHeader
        crumbs={[{ label: "首页", to: "/" }, { label: "关于我们" }]}
        eyebrow="关于我们"
        title="一个把「保护投稿人」写在第一条的爆料平台"
        desc={`${site.name}从 ${site.since} 年开始接收读者投稿。我们不做标题党，也不追求第一时间放料——我们更在意一条料发出来之后，投稿人是否还安全。`}
      />

      <section className="mx-auto w-full max-w-shell px-4 py-10 md:px-6 md:py-14">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">我们是谁</h2>
            <div className="mt-5 space-y-4 text-sm leading-loose text-foreground-700 md:text-base">
              <p>
                {site.name}是一个由编辑与核实人员组成的小团队。我们不生产新闻，只做两件事：接收投稿人手里的线索，然后尽可能扎实地把它们核实清楚。
              </p>
              <p>
                平台上线至今，累计收到超过一万两千条线索。其中大量内容因为证据不足、涉及隐私或与公众利益无关，最终没有发布——这是我们有意保留的取舍。与其快速堆量，不如让每一条放出来的爆料都经得起回看。
              </p>
              <p>
                我们相信，愿意把内幕交出来的人，多数不是为了钱，而是觉得这件事该被知道。既然如此，平台的责任就是让他们不必为此付出代价。
              </p>
            </div>

            <h2 className="mt-12 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">内容原则</h2>
            <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {principles.map((item) => (
                <div key={item.title} className="rounded-lg border border-foreground-950/10 bg-background-50 p-5">
                  <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-50 text-primary-600">
                    <i className={`${item.icon} text-xl`} aria-hidden="true"></i>
                  </span>
                  <h3 className="mt-3.5 font-heading text-base font-semibold text-foreground-950">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <aside className="space-y-5 lg:col-span-5">
            <div className="rounded-lg border border-foreground-950/10 bg-background-100 p-6">
              <h2 className="font-heading text-lg font-semibold text-foreground-950">网站定位</h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                我们不是媒体机构，也不是裁判。平台的角色更像一个中转站：把碎片化的线索收拢、核对、标注状态，再交到读者面前。
              </p>
              <dl className="mt-5 space-y-3 border-t border-foreground-950/10 pt-4 text-sm">
                <div className="flex items-center justify-between">
                  <dt className="text-foreground-500">上线时间</dt>
                  <dd className="font-medium text-foreground-900">{site.since} 年</dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-foreground-500">累计线索</dt>
                  <dd className="font-medium text-foreground-900">12,800+ 条</dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-foreground-500">初筛时效</dt>
                  <dd className="font-medium text-foreground-900">{site.handlingHours}</dd>
                </div>
              </dl>
            </div>

            <div className="rounded-lg border border-primary-200 bg-primary-50 p-6">
              <h2 className="font-heading text-lg font-semibold text-primary-800">联系我们</h2>
              <ul className="mt-4 space-y-3 text-sm">
                <li className="flex items-center gap-3 text-foreground-700">
                  <i className="ri-mail-send-line text-lg text-primary-600" aria-hidden="true"></i>
                  投稿线索：{site.email}
                </li>
                <li className="flex items-center gap-3 text-foreground-700">
                  <i className="ri-mail-line text-lg text-primary-600" aria-hidden="true"></i>
                  内容更正：zhengming@kanliaobaoliao.com
                </li>
                <li className="flex items-center gap-3 text-foreground-700">
                  <i className="ri-time-line text-lg text-primary-600" aria-hidden="true"></i>
                  反馈时效：{site.handlingHours}
                </li>
              </ul>
              <Link
                to="/submit"
                className="mt-5 flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-600 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700"
              >
                <i className="ri-mail-send-line text-base" aria-hidden="true"></i>
                进入投稿通道
              </Link>
            </div>
          </aside>
        </div>
      </section>

      <section className="border-t border-foreground-950/10 bg-secondary-50">
        <div className="mx-auto w-full max-w-shell px-4 py-14 md:px-6 md:py-16">
          <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">投稿人怎么说</h2>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            下面这些反馈来自已完成核实的投稿人，出于保护隐私的考虑，我们只保留了他们自己选定的代号。
          </p>
          <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
            {testimonials.map((item) => (
              <figure key={item.id} className="flex h-full flex-col rounded-lg border border-secondary-200 bg-background-50 p-6">
                <i className="ri-double-quotes-l text-2xl text-secondary-500" aria-hidden="true"></i>
                <blockquote className="mt-3 flex-1 text-sm leading-relaxed text-foreground-700">
                  {item.quote}
                </blockquote>
                <figcaption className="mt-5 border-t border-foreground-950/10 pt-4">
                  <span className="block text-sm font-medium text-foreground-950">{item.author}</span>
                  <span className="mt-0.5 block text-xs text-foreground-500">{item.role}</span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}